import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

function TermsAndConditions() {
  return (
    <div className="min-h-screen bg-white p-6">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8 pt-8">
          <Link to="/" className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-semibold text-sm transition-colors mb-6">
            <ArrowLeft size={18} />
            Back to Valuation
          </Link>
          <div className="text-center mb-4">
            <img src="sell-my-car-teesside-logo.png" alt="Sell My Car Teesside Logo" className="mx-auto w-48 h-24 mb-3 object-contain" />
          </div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2 text-center">Terms & Conditions</h1>
          <p className="text-slate-500 text-sm text-center">Last updated: February 2026</p>
        </div>

        <div className="bg-white rounded-3xl p-8 shadow-xl border border-slate-200 mb-8 space-y-8 text-slate-700 text-sm leading-relaxed">
          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-3">1. Introduction</h2>
            <div className="space-y-2">
              <p>1.1. These Terms & Conditions govern the sale of vehicles through this website, operated by Teesside Autos Ltd ("we", "us", "our"), company number 15129726, registered in England and Wales.</p>
              <p>1.2. By submitting your vehicle details and accepting a valuation, you agree to be bound by these Terms.</p>
              <p>1.3. This website is a trading name of Teesside Autos Ltd.</p>
            </div>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-3">2. Offers and Valuation</h2>
            <div className="space-y-2">
              <p>2.1. Any online valuation provided through this website is an indicative estimate only and is subject to inspection and verification.</p>
              <p>2.2. Online valuations are valid for 14 days unless withdrawn earlier.</p>
              <p>2.3. The final offer will be confirmed following physical inspection and verification of the vehicle's condition, documentation, mileage, and history.</p>
              <p>2.4. The final offer may be adjusted if the vehicle:</p>
              <ul className="list-disc ml-6 space-y-1">
                <li>Has undisclosed damage</li>
                <li>Has mileage different from that stated</li>
                <li>Contains missing parts or keys</li>
                <li>Has undeclared modifications</li>
                <li>Has undisclosed accident or insurance write-off history</li>
                <li>Is not in substantially the same condition as described at the time of valuation</li>
              </ul>
              <p>2.5. We reserve the right to withdraw or amend any offer at our discretion prior to completion.</p>
              <p>2.6. Offers are non-transferable and valid only for the named seller and specified vehicle.</p>
            </div>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-3">3. Seller Warranties</h2>
            <p className="mb-2">By submitting your vehicle for sale, you confirm and warrant that:</p>
            <div className="space-y-2">
              <p>3.1. You are the legal owner of the vehicle or have full authority to sell it.</p>
              <p>3.2. The vehicle is not stolen and is free from liens, charges, or encumbrances unless declared.</p>
              <p>3.3. Any outstanding finance has been declared.</p>
              <p>3.4. The mileage reading is accurate to the best of your knowledge.</p>
              <p>3.5. The vehicle has not been declared an insurance write-off unless disclosed.</p>
              <p>3.6. The vehicle is sold as-is, except for representations explicitly made to us.</p>
              <p>3.7. The vehicle will remain insured for road use until collection.</p>
              <p>3.8. The vehicle will be in substantially the same condition at collection as described during valuation.</p>
              <p>3.9. You will notify us immediately if the vehicle develops a fault or suffers damage before collection.</p>
            </div>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-3">4. Identity & Fraud Prevention</h2>
            <div className="space-y-2">
              <p>4.1. We reserve the right to conduct identity verification, ownership checks, finance checks, and vehicle history checks.</p>
              <p>4.2. We may refuse to proceed where we reasonably suspect fraud, misrepresentation, or unlawful activity.</p>
              <p>4.3. We reserve the right to request additional documentation where necessary to comply with anti-money laundering regulations.</p>
            </div>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-3">5. Payment</h2>
            <div className="space-y-2">
              <p>5.1. Payment will be made via bank transfer once inspection is complete and all required documentation has been verified.</p>
              <p>5.2. Where the vehicle is subject to outstanding finance, we may settle the finance directly with the lender.</p>
              <p>5.3. If the finance settlement amount exceeds the agreed purchase price, the seller must pay the shortfall prior to completion.</p>
              <p>5.4. Payment is subject to confirmation of finance clearance where applicable.</p>
              <p>5.5. We are not liable for delays caused by banking systems, lender processes, identity verification, or finance settlement procedures.</p>
              <p>5.6. We do not charge administration or collection fees. The agreed purchase price is the amount you will receive, subject only to finance settlement where applicable.</p>
            </div>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-3">6. Collection and Risk</h2>
            <div className="space-y-2">
              <p>6.1. Risk in the vehicle remains with the seller until the vehicle has been collected and payment has been confirmed as received in the seller's bank account.</p>
              <p>6.2. Upon completion, we will notify the DVLA of the change of ownership electronically where possible. The seller remains responsible for ensuring the DVLA is properly notified if required.</p>
              <p>6.3. The seller is responsible for removing all personal belongings from the vehicle prior to collection. We accept no responsibility for any items left in the vehicle after collection.</p>
              <p>6.4. Any private or cherished registration number must be retained by the seller prior to completion. We are not responsible for retaining or transferring private registration numbers unless agreed in writing.</p>
            </div>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-3">7. Documentation Requirements</h2>
            <p className="mb-2">Before collection, the seller must provide:</p>
            <ul className="list-disc ml-6 space-y-1">
              <li>V5C logbook (where available)</li>
              <li>Valid photo identification</li>
              <li>Proof of address</li>
              <li>All vehicle keys</li>
              <li>Any service history declared during valuation</li>
            </ul>
            <p className="mt-2">Failure to provide required documentation may result in withdrawal or revision of the offer.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-3">8. Cancellations</h2>
            <div className="space-y-2">
              <p>8.1. You may cancel the sale at any time prior to collection without penalty.</p>
              <p>8.2. If collection has been arranged and cancelled without reasonable notice, we reserve the right to decline future offers.</p>
              <p>8.3. Once the vehicle has been collected and payment made, the contract is complete and cannot be reversed except where required by law.</p>
            </div>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-3">9. Operating Area</h2>
            <div className="space-y-2">
              <p>9.1. We operate within a 50-mile radius of TS18 2ER.</p>
              <p>9.2. Vehicles outside this radius may be declined at our discretion.</p>
            </div>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-3">10. Liability</h2>
            <div className="space-y-2">
              <p>10.1. We accept no liability for:</p>
              <ul className="list-disc ml-6 space-y-1">
                <li>Misrepresentation of vehicle details</li>
                <li>Any reduction in valuation following inspection</li>
                <li>Any consequential or indirect losses</li>
              </ul>
              <p>10.2. Our total liability shall be limited to the agreed purchase price of the vehicle.</p>
              <p>10.3. Nothing in these Terms limits or excludes liability for fraud or any liability which cannot be excluded under English law.</p>
            </div>
          </section>

          <section>
            <h2 className="text-lg font-bold text-slate-900 mb-3">11. General</h2>
            <div className="space-y-2">
              <p>11.1. These Terms are governed by the laws of England and Wales.</p>
              <p>11.2. Any disputes shall be subject to the exclusive jurisdiction of the courts of England and Wales.</p>
              <p>11.3. We reserve the right to update these Terms at any time. The version displayed on the website at the time of submission shall apply.</p>
            </div>
          </section>
        </div>

        <div className="text-center py-6">
          <p className="text-slate-500 text-sm">
            Powered by <a href="https://www.teessideautos.co.uk/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">TeessideAutos</a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default TermsAndConditions;
